/// <amd-module name="Agile/Taskboard/CustomColumn/State/CustomColumnPanelReducer" />
import * as Resources from "Agile/Taskboard/CustomColumn/Resources";
import produce from "immer";
import { localeFormat, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { CustomColumnActions, CustomColumnActionTypes } from "./CustomColumnActions";
import {
    ColumnToStateMap,
    IColumn,
    ICustomColumnState,
    MAX_COLUMN_LIMIT,
    WorkItemStateCategory,
    WorkItemTypeToStateMap
} from "./CustomColumnContracts";

export function customColumnPanelReducer(state: ICustomColumnState, action: CustomColumnActions) {
    const lastIndex = state.columns.length - 1;

    const updatedState = produce(state, draft => {
        switch (action.type) {
            case CustomColumnActionTypes.Validate: {
                _validate(draft);
                break;
            }
            case CustomColumnActionTypes.SelectColumn: {
                const { columnIndex } = action.payload!;
                draft.selectedColumnIndex = columnIndex;
                break;
            }
            case CustomColumnActionTypes.AddColumn: {
                if (state.columns.length < MAX_COLUMN_LIMIT) {
                    const uniqueColumnName = _getUniqueColumnName(state.columns);
                    const newColumn = {
                        id: Date.now().toString() + uniqueColumnName,
                        name: uniqueColumnName,
                        order: lastIndex,
                        isNew: true
                    };
                    draft.columns.splice(lastIndex, 0, newColumn);
                    draft.normalizedColumnIdToWorkItemTypeStateMap[newColumn.id] = {};

                    // map work item types to the column
                    for (const workItemType of Object.keys(draft.normalizedTaskWorkItemTypesToStateMap)) {
                        _mapWorkItemState(
                            draft.normalizedTaskWorkItemTypesToStateMap,
                            workItemType,
                            draft.normalizedColumnIdToWorkItemTypeStateMap,
                            newColumn.id,
                            [WorkItemStateCategory.InProgress]
                        );
                    }

                    draft.selectedColumnIndex = lastIndex;
                    _reorder(draft.columns);
                    draft.isDirty = true;
                } else {
                    draft.addNewColumnFailed = true;
                }
                break;
            }
            case CustomColumnActionTypes.RemoveColumn: {
                const { columnId } = action.payload!;
                const index = _findIndexById(state.columns, columnId);
                if (index < lastIndex || index > 0) {
                    const column = draft.columns[index];
                    delete draft.normalizedColumnIdToWorkItemTypeStateMap[column.id];
                    draft.columns.splice(index, 1);
                } else {
                    throw new Error("Not allowed to remove first or last column.");
                }

                _reorder(draft.columns);
                draft.addNewColumnFailed = false;
                draft.isDirty = true;
                break;
            }
            case CustomColumnActionTypes.RenameColumn: {
                const { columnId, newColumnName } = action.payload!;
                const index = _findIndexById(state.columns, columnId);
                if (!_eq(state.columns[index].name, newColumnName)) {
                    draft.columns[index].name = newColumnName;
                    draft.isDirty = true;
                }
                break;
            }
            case CustomColumnActionTypes.ReorderColumn: {
                const { columnId } = action.payload!;
                let { newIndex } = action.payload!;
                if (newIndex > 0) {
                    const oldIndex = _findIndexById(state.columns, columnId);
                    if (oldIndex === -1) {
                        // Can't reorder as the column Id is invalid
                        throw new Error("Can not find the column.");
                    }

                    if (newIndex > oldIndex) {
                        newIndex -= 1; // new index includes self so decrement to take that into account
                    }

                    draft.selectedColumnIndex = newIndex;
                    if (newIndex < lastIndex) {
                        // Move the column to new index
                        const column = draft.columns[oldIndex];
                        draft.columns.splice(oldIndex, 1);
                        draft.columns.splice(newIndex, 0, column);
                        _reorder(draft.columns);
                        draft.isDirty = true;
                    } else {
                        throw new Error("Can not reorder as last column.");
                    }
                } else {
                    throw new Error("Can not reorder as first column.");
                }
                break;
            }
            case CustomColumnActionTypes.Saving: {
                draft.savingError = undefined;
                draft.saving = true;
                break;
            }
            case CustomColumnActionTypes.SaveFailed: {
                const { error } = action.payload!;
                draft.saving = false;
                draft.savingError = error;
                break;
            }
            case CustomColumnActionTypes.UpdateState: {
                const { columnId, workItemType, state: stateName } = action.payload!;
                const index = _findIndexById(state.columns, columnId);
                if (index === -1) {
                    throw new Error("Invalid column Id");
                }

                const stateMap = state.normalizedColumnIdToWorkItemTypeStateMap[columnId];
                if (!stateMap) {
                    draft.normalizedColumnIdToWorkItemTypeStateMap[columnId] = {};
                }

                draft.normalizedColumnIdToWorkItemTypeStateMap[columnId][workItemType.toLocaleLowerCase()] = {
                    stateNameKey: stateName.toLocaleLowerCase()
                };
                draft.isDirty = true;
                break;
            }
        }
    });

    // Validate the just updated state
    return produce(updatedState, draft => {
        _validate(draft);
    });
}

function _validate(draft: ICustomColumnState): boolean {
    const { normalizedColumnIdToWorkItemTypeStateMap, normalizedTaskWorkItemTypesToStateMap, columns } = draft;
    draft.isValid = true;

    // Validate column name, not empty does not contain illegal chars, is = 128 char long, and not duplicate
    for (let i = 0; i < columns.length; i++) {
        const column = columns[i];
        const columnName = column.name;
        columns[i].stateMappingError = undefined;
        if (!columnName || columnName.trim().length === 0) {
            // Empty?
            columns[i].error = Resources.ValidationError_EmptyColumnName;
            draft.isValid = false;
        } else if (columnName.trim().length > 128) {
            // Too long?
            columns[i].error = Resources.ValidationError_ColumnNameTooLong;
            draft.isValid = false;
        } else if (IllegalNameChars.some(ic => columnName.indexOf(ic) >= 0)) {
            // Are there any illegal chars
            columns[i].error = localeFormat(Resources.ValidationError_IllegalCharacters, IllegalNameChars.join(""));
            draft.isValid = false;
        } else if (columns.findIndex((col, index) => index !== i && _eq(col.name.trim(), columnName.trim())) !== -1) {
            // Are there any duplicates?
            columns[i].error = Resources.ValidationError_DuplicateColumnName;
            draft.isValid = false;
        } else {
            columns[i].error = undefined;
        }
    }

    // Remove work item types that do not belong to Taskboard any more e.g. due to bugs behavior or process changes
    const allowedWorkItemTypeKeys = Object.keys(normalizedTaskWorkItemTypesToStateMap);
    const columnIds = Object.keys(normalizedColumnIdToWorkItemTypeStateMap);

    for (const columnId of columnIds) {
        const columnMapping = normalizedColumnIdToWorkItemTypeStateMap[columnId];
        const columnWorkItemTypes = Object.keys(columnMapping);
        for (const columnWorkItemType of columnWorkItemTypes) {
            // clear the error if any
            if (normalizedColumnIdToWorkItemTypeStateMap[columnId][columnWorkItemType].error) {
                normalizedColumnIdToWorkItemTypeStateMap[columnId][columnWorkItemType].error = undefined;
            }
            const index = allowedWorkItemTypeKeys.findIndex(allowedWit => _eq(allowedWit, columnWorkItemType));
            // Remove any work item type that does not belong to backlog anymore
            if (index === -1) {
                delete normalizedColumnIdToWorkItemTypeStateMap[columnId][columnWorkItemType];
                draft.isDirty = true;
            } else {
                // Mark any state mappings that are not part of wit anymore
                const allowedStates = Object.keys(normalizedTaskWorkItemTypesToStateMap[columnWorkItemType]);
                const columnState = columnMapping[columnWorkItemType];
                const stateIndex = allowedStates.findIndex(allowedState => _eq(columnState.stateNameKey, allowedState));
                if (stateIndex === -1) {
                    const errorMessage = localeFormat(
                        Resources.ValidationError_InvalidState,
                        draft.witKeyNameMap[columnWorkItemType] || columnWorkItemType,
                        draft.stateKeyNameMap[columnState.stateNameKey] || columnState.stateNameKey
                    );
                    _setStateError(draft, columnId, columnWorkItemType, errorMessage);
                }
            }
        }
    }

    // Add work item types that are newly introduced and don't have any states mapped e.g. due to bugs behavior or process changes
    for (const allowedWorkItemType of allowedWorkItemTypeKeys) {
        for (const columnId of columnIds) {
            const columnMapping = normalizedColumnIdToWorkItemTypeStateMap[columnId];
            const columnWorkItemTypes = Object.keys(columnMapping);
            const witIndex = columnWorkItemTypes.findIndex(columnWit => _eq(columnWit, allowedWorkItemType));
            if (witIndex === -1) {
                const columnIndex = columns.findIndex(col => col.id === columnId);
                _mapWorkItemState(
                    normalizedTaskWorkItemTypesToStateMap,
                    allowedWorkItemType,
                    normalizedColumnIdToWorkItemTypeStateMap,
                    columnId,
                    columnIndex === 0
                        ? [WorkItemStateCategory.Proposed, WorkItemStateCategory.InProgress]
                        : columnIndex === columns.length - 1
                        ? [WorkItemStateCategory.Completed]
                        : [WorkItemStateCategory.InProgress]
                );
                draft.isDirty = true;
            }
        }
    }

    // Validate that all work item states are mapped to atleast on column
    draft.unmappedStateKeys = {};
    for (const allowedWorkItemType of allowedWorkItemTypeKeys) {
        const allowedStateKeys = Object.keys(normalizedTaskWorkItemTypesToStateMap[allowedWorkItemType]);
        const unmappedStatesKeys = allowedStateKeys.filter(
            stateKey => !_isMapped(normalizedColumnIdToWorkItemTypeStateMap, allowedWorkItemType, stateKey)
        );
        // Are there any states not mapped to any columns?
        if (unmappedStatesKeys.length > 0) {
            draft.unmappedStateKeys[allowedWorkItemType] = unmappedStatesKeys;
            draft.isValid = false;
        }
    }

    // Validate that the first column is mapped to Proposed and last column is mapped to Completed states
    _validateStateCategory(draft, columns[0], /** Last Column */ false);
    _validateStateCategory(draft, columns[columns.length - 1], /** Last Column */ true);

    return draft.isValid;
}

/** Map to first In-Progress state */
function _mapWorkItemState(
    normalizedTaskWorkItemTypesToStateMap: WorkItemTypeToStateMap,
    allowedWorkItemType: string,
    normalizedColumnIdToWorkItemTypeStateMap: ColumnToStateMap,
    columnId: string,
    allowedStateCategories: WorkItemStateCategory[]
) {
    // Get first In Progress state and assign to the work item type
    const allWitStates = Object.keys(normalizedTaskWorkItemTypesToStateMap[allowedWorkItemType]);
    const allowedStates = allWitStates.filter(stateKey => allowedStateCategories.findIndex(cat => normalizedTaskWorkItemTypesToStateMap[allowedWorkItemType][stateKey] === cat) >=0);
    normalizedColumnIdToWorkItemTypeStateMap[columnId][allowedWorkItemType] = {
        stateNameKey: allowedStates.length > 0 ? allowedStates[0] : allWitStates[0],
        error: undefined
    };
}

/**
 * Validates that all mapped states in given column belong to expectedCategory
 */
function _validateStateCategory(draft: ICustomColumnState, column: IColumn, lastColumn: boolean) {
    const columnMappings = draft.normalizedColumnIdToWorkItemTypeStateMap[column.id];
    for (const workItemTypeName of Object.keys(columnMappings)) {
        const stateMapping = columnMappings[workItemTypeName];
        const stateCategory = draft.normalizedTaskWorkItemTypesToStateMap[workItemTypeName][stateMapping.stateNameKey];
        if (!stateMapping.error) {

            if(lastColumn && stateCategory !== WorkItemStateCategory.Completed ) {
                _setStateError(draft, column.id, workItemTypeName, Resources.ValidationError_CompletedState);
            } else if(!lastColumn && stateCategory !== WorkItemStateCategory.Proposed && stateCategory !== WorkItemStateCategory.InProgress) {
                _setStateError(draft, column.id, workItemTypeName, Resources.ValidationError_ProposedInProgressState);
            }

        }
    }
}

function _setStateError(draft: ICustomColumnState, columnId: string, workItemTypeName: string, errorMessage: string) {
    draft.normalizedColumnIdToWorkItemTypeStateMap[columnId][workItemTypeName].error = errorMessage;
    const columnIndex = draft.columns.findIndex(col => _eq(col.id, columnId));
    if (!draft.columns[columnIndex].error) {
        draft.columns[columnIndex].stateMappingError = errorMessage;
    }
    draft.isValid = false;
}

/**
 * Checks if the given state for work item type is mapped to atleast one column
 */
function _isMapped(columnIdToWorkItemTypeStateMap: ColumnToStateMap, workItemTypeName: string, stateName: string) {
    const columnIds = Object.keys(columnIdToWorkItemTypeStateMap);
    for (const columnId of columnIds) {
        const column = columnIdToWorkItemTypeStateMap[columnId];
        if (_eq(column[workItemTypeName].stateNameKey, stateName)) {
            return true;
        }
    }
    return false;
}

function _findIndex(columns: IColumn[], columnName: string): number {
    return columns.findIndex((col: IColumn) => _eq(col.name, columnName));
}

function _findIndexById(columns: IColumn[], columnId: string): number {
    return columns.findIndex((col: IColumn) => _eq(col.id, columnId));
}

function _getUniqueColumnName(existingColumns: IColumn[]): string {
    let columnName = "";
    let count = 0;
    while (count < 1000) {
        if (count === 0) {
            columnName = Resources.NewColumn;
        } else {
            columnName = Resources.NewColumn + count;
        }
        const index = _findIndex(existingColumns, columnName);
        if (index === -1) {
            return columnName;
        }
        count++;
    }
    throw new Error("Too many duplicate columns.");
}

function _eq(str1: string, str2: string) {
    return localeIgnoreCaseComparer(str1, str2) === 0;
}

/**
 * Fixes the order of the columns
 */
function _reorder(columns: IColumn[]) {
    columns.forEach((col, index) => (col.order = index));
}

const IllegalNameChars = ["'", ",", ";", "~", ":", "\\", "*", "|", "?", '"', "&", "%", "$", "!", "+", "=", "[", "]", "{", "}", "<", ">", "\u0E4C"];
